import { useCallback, useEffect, useRef, useState } from 'react';
import { Client } from '@stomp/stompjs';
import type { IMessage } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { config } from '../config';
import unifiedAuth from '../utils/unifiedAuth';

interface WebRTCSignal {
  type: 'OFFER' | 'ANSWER' | 'ICE_CANDIDATE' | 'BROADCASTER_READY' | 'BROADCASTER_NOT_READY' | 'VIEWER_REQUEST';
  streamId: string;
  fromUserId?: string;
  toUserId?: string;
  sdp?: string;
  candidate?: string;
  sdpMid?: string;
  sdpMLineIndex?: number;
}

interface UseWebRTCOptions {
  streamId: string;
  isBroadcaster: boolean;
  onRemoteStream?: (stream: MediaStream) => void;
  onLocalStream?: (stream: MediaStream) => void;
  onConnectionStateChange?: (state: RTCPeerConnectionState) => void;
  onError?: (error: Error) => void;
  onRecordingStateChange?: (isRecording: boolean) => void;
}

interface PeerConnectionEntry {
  connection: RTCPeerConnection;
  viewerId: string;
}

const ICE_SERVERS: RTCConfiguration = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' },
    { urls: 'stun:stun3.l.google.com:19302' },
    { urls: 'stun:stun4.l.google.com:19302' },
  ],
};

export const useWebRTC = ({
  streamId,
  isBroadcaster,
  onRemoteStream,
  onLocalStream,
  onConnectionStateChange,
  onError,
  onRecordingStateChange,
}: UseWebRTCOptions) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isBroadcasting, setIsBroadcasting] = useState(false);
  const [connectionState, setConnectionState] = useState<RTCPeerConnectionState>('new');
  const [viewerCount, setViewerCount] = useState(0);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordedChunks, setRecordedChunks] = useState<Blob[]>([]);

  /**
   * The caller's callbacks, held by reference rather than by identity.
   *
   * This is the fix for a connection loop that took the site down. The effect that builds
   * the STOMP client listed these callbacks in its dependencies, and GoLivePage passes
   * them as inline arrow functions - a new identity on every render. So: render, effect
   * tears down and rebuilds the client, client.activate() opens a socket, onConnect sets
   * state, state change re-renders, and round again. One WebSocket per iteration until
   * the browser gave up with "Insufficient resources", and in production until nginx's
   * per-IP connection limit returned 503 for every request from that browser.
   *
   * Reading them from a ref means the effect can depend on nothing but the stream and the
   * role, while still calling whatever the caller most recently passed.
   * useLiveStreamWebSocket already does exactly this; this hook did not.
   */
  const callbacksRef = useRef({
    onRemoteStream,
    onLocalStream,
    onConnectionStateChange,
    onError,
    onRecordingStateChange,
  });

  useEffect(() => {
    callbacksRef.current = {
      onRemoteStream,
      onLocalStream,
      onConnectionStateChange,
      onError,
      onRecordingStateChange,
    };
  });

  const stompClientRef = useRef<Client | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const screenStreamRef = useRef<MediaStream | null>(null);
  const originalVideoTrackRef = useRef<MediaStreamTrack | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]); // Store chunks during recording
  
  // For broadcaster: map of viewer IDs to their peer connections
  const peerConnectionsRef = useRef<Map<string, PeerConnectionEntry>>(new Map());
  
  // For viewer: single peer connection to broadcaster
  const viewerPeerConnectionRef = useRef<RTCPeerConnection | null>(null);

  // Update viewer count helper
  const updateViewerCount = useCallback(() => {
    setViewerCount(peerConnectionsRef.current.size);
  }, []);

  // Create a peer connection for a specific viewer (broadcaster side)
  const createPeerConnectionForViewer = useCallback((viewerId: string): RTCPeerConnection => {
    const pc = new RTCPeerConnection(ICE_SERVERS);

    pc.onicecandidate = (event) => {
      if (event.candidate && stompClientRef.current?.connected) {
        const signal: WebRTCSignal = {
          type: 'ICE_CANDIDATE',
          streamId,
          toUserId: viewerId,
          candidate: event.candidate.candidate,
          sdpMid: event.candidate.sdpMid || undefined,
          sdpMLineIndex: event.candidate.sdpMLineIndex ?? undefined,
        };
        stompClientRef.current.publish({
          destination: `/app/stream/${streamId}/webrtc/ice-candidate`,
          body: JSON.stringify(signal),
        });
      }
    };

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
        peerConnectionsRef.current.delete(viewerId);
        updateViewerCount();
      }
    };

    // Add local tracks to the connection
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => {
        pc.addTrack(track, localStreamRef.current!);
      });
    }

    peerConnectionsRef.current.set(viewerId, { connection: pc, viewerId });
    updateViewerCount();
    
    return pc;
  }, [streamId, updateViewerCount]);

  // Create peer connection for viewer side
  const createViewerPeerConnection = useCallback((): RTCPeerConnection => {
    const pc = new RTCPeerConnection(ICE_SERVERS);

    pc.onicecandidate = (event) => {
      if (event.candidate && stompClientRef.current?.connected) {
        const signal: WebRTCSignal = {
          type: 'ICE_CANDIDATE',
          streamId,
          candidate: event.candidate.candidate,
          sdpMid: event.candidate.sdpMid || undefined,
          sdpMLineIndex: event.candidate.sdpMLineIndex ?? undefined,
        };
        stompClientRef.current.publish({
          destination: `/app/stream/${streamId}/webrtc/ice-candidate`,
          body: JSON.stringify(signal),
        });
      }
    };

    pc.ontrack = (event) => {
      // Through the ref like the others. Read from the closure it would be whatever was
      // passed on the first render, and removing it from this callback's dependencies -
      // which is what stops the connection loop - is exactly what makes that stale.
      const { onRemoteStream: handleRemote } = callbacksRef.current;
      if (event.streams[0] && handleRemote) {
        handleRemote(event.streams[0]);
      }
    };

    pc.onconnectionstatechange = () => {
      const state = pc.connectionState;
      setConnectionState(state);
      callbacksRef.current.onConnectionStateChange?.(state);
    };

    viewerPeerConnectionRef.current = pc;
    return pc;
  }, [streamId]);

  // Handle incoming WebRTC signals
  const handleWebRTCSignal = useCallback(async (signal: WebRTCSignal) => {
    try {
      if (isBroadcaster) {
        // Broadcaster handling
        switch (signal.type) {
          case 'VIEWER_REQUEST': {
            // Create offer for the requesting viewer
            const viewerId = signal.fromUserId!;
            const pc = createPeerConnectionForViewer(viewerId);
            
            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);
            
            const offerSignal: WebRTCSignal = {
              type: 'OFFER',
              streamId,
              toUserId: viewerId,
              sdp: offer.sdp,
            };
            
            stompClientRef.current?.publish({
              destination: `/app/stream/${streamId}/webrtc/offer`,
              body: JSON.stringify(offerSignal),
            });
            break;
          }
          
          case 'ANSWER': {
            const viewerId = signal.fromUserId!;
            const entry = peerConnectionsRef.current.get(viewerId);
            if (entry && signal.sdp) {
              await entry.connection.setRemoteDescription({
                type: 'answer',
                sdp: signal.sdp,
              });
            }
            break;
          }
          
          case 'ICE_CANDIDATE': {
            const viewerId = signal.fromUserId!;
            const entry = peerConnectionsRef.current.get(viewerId);
            if (entry && signal.candidate) {
              await entry.connection.addIceCandidate({
                candidate: signal.candidate,
                sdpMid: signal.sdpMid,
                sdpMLineIndex: signal.sdpMLineIndex,
              });
            }
            break;
          }
        }
      } else {
        // Viewer handling
        switch (signal.type) {
          case 'BROADCASTER_READY': {
            // Broadcaster is ready, request stream
            stompClientRef.current?.publish({
              destination: `/app/stream/${streamId}/webrtc/request-stream`,
              body: JSON.stringify({ streamId }),
            });
            break;
          }
          
          case 'BROADCASTER_NOT_READY': {
            // Broadcaster is not ready yet, wait for BROADCASTER_READY signal
            break;
          }
          
          case 'OFFER': {
            const pc = viewerPeerConnectionRef.current || createViewerPeerConnection();
            
            if (signal.sdp) {
              await pc.setRemoteDescription({
                type: 'offer',
                sdp: signal.sdp,
              });
              
              const answer = await pc.createAnswer();
              await pc.setLocalDescription(answer);
              
              const answerSignal: WebRTCSignal = {
                type: 'ANSWER',
                streamId,
                toUserId: signal.fromUserId,
                sdp: answer.sdp,
              };
              
              stompClientRef.current?.publish({
                destination: `/app/stream/${streamId}/webrtc/answer`,
                body: JSON.stringify(answerSignal),
              });
            }
            break;
          }
          
          case 'ICE_CANDIDATE': {
            const pc = viewerPeerConnectionRef.current;
            if (pc && signal.candidate) {
              await pc.addIceCandidate({
                candidate: signal.candidate,
                sdpMid: signal.sdpMid,
                sdpMLineIndex: signal.sdpMLineIndex,
              });
            }
            break;
          }
        }
      }
    } catch (error) {
      console.error('Error handling WebRTC signal:', error);
      callbacksRef.current.onError?.(error instanceof Error ? error : new Error(String(error)));
    }
  }, [isBroadcaster, streamId, createPeerConnectionForViewer, createViewerPeerConnection]);

  // Start broadcasting with local stream
  const startBroadcast = useCallback(async (stream: MediaStream) => {
    // Wait for WebSocket connection if not connected yet
    if (!stompClientRef.current?.connected) {
      // Wait up to 5 seconds for connection
      let attempts = 0;
      while (!stompClientRef.current?.connected && attempts < 50) {
        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;
      }
      
      if (!stompClientRef.current?.connected) {
        throw new Error('WebSocket connection timeout. Please try again.');
      }
    }

    localStreamRef.current = stream;
    
    // Register as broadcaster
    stompClientRef.current.publish({
      destination: `/app/stream/${streamId}/webrtc/register-broadcaster`,
      body: JSON.stringify({ streamId }),
    });
    
    setIsBroadcasting(true);
    callbacksRef.current.onLocalStream?.(stream);
  }, [streamId]);

  // Stop broadcasting
  const stopBroadcast = useCallback(() => {
    // Stop recording if active
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    
    // Stop screen sharing if active
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach((track) => track.stop());
      screenStreamRef.current = null;
    }
    
    // Close all peer connections
    peerConnectionsRef.current.forEach((entry) => {
      entry.connection.close();
    });
    peerConnectionsRef.current.clear();
    
    // Stop local stream
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => track.stop());
      localStreamRef.current = null;
    }
    
    setIsBroadcasting(false);
    setViewerCount(0);
    setIsScreenSharing(false);
    setIsRecording(false);
  }, []);

  // Stop screen sharing (defined first so it can be used in startScreenShare)
  const stopScreenShare = useCallback(() => {
    if (!screenStreamRef.current || !originalVideoTrackRef.current || !localStreamRef.current) {
      return;
    }

    const screenTrack = screenStreamRef.current.getVideoTracks()[0];
    
    // Stop screen stream
    screenStreamRef.current.getTracks().forEach((track) => track.stop());
    screenStreamRef.current = null;
    
    // Restore original video track
    localStreamRef.current.removeTrack(screenTrack);
    localStreamRef.current.addTrack(originalVideoTrackRef.current);
    
    // Replace track in all peer connections
    peerConnectionsRef.current.forEach((entry) => {
      const sender = entry.connection.getSenders().find(s => s.track?.kind === 'video');
      if (sender && originalVideoTrackRef.current) {
        sender.replaceTrack(originalVideoTrackRef.current);
      }
    });

    originalVideoTrackRef.current = null;
    setIsScreenSharing(false);
  }, []);

  // Start screen sharing
  const startScreenShare = useCallback(async () => {
    if (!localStreamRef.current) {
      throw new Error('No local stream available');
    }

    try {
      const screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          cursor: 'always',
          displaySurface: 'monitor',
        } as MediaTrackConstraints,
        audio: false,
      });

      const screenTrack = screenStream.getVideoTracks()[0];
      screenStreamRef.current = screenStream;
      
      // Store original video track
      const originalTrack = localStreamRef.current.getVideoTracks()[0];
      originalVideoTrackRef.current = originalTrack;
      
      // Replace video track in local stream
      localStreamRef.current.removeTrack(originalTrack);
      localStreamRef.current.addTrack(screenTrack);
      
      // Replace track in all peer connections
      peerConnectionsRef.current.forEach((entry) => {
        const sender = entry.connection.getSenders().find(s => s.track?.kind === 'video');
        if (sender) {
          sender.replaceTrack(screenTrack);
        }
      });

      // Handle when user stops screen share via browser UI
      screenTrack.onended = () => {
        stopScreenShare();
      };

      setIsScreenSharing(true);
      
      return screenStream;
    } catch (error) {
      console.error('Error starting screen share:', error);
      throw error;
    }
  }, [stopScreenShare]);

  // Start recording
  const startRecording = useCallback(() => {
    if (!localStreamRef.current) {
      throw new Error('No local stream available');
    }

    const options = { mimeType: 'video/webm;codecs=vp9,opus' };
    
    // Fallback to other codecs if vp9 is not supported
    if (!MediaRecorder.isTypeSupported(options.mimeType)) {
      if (MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')) {
        options.mimeType = 'video/webm;codecs=vp8,opus';
      } else if (MediaRecorder.isTypeSupported('video/webm')) {
        options.mimeType = 'video/webm';
      } else {
        throw new Error('No supported recording format available');
      }
    }

    // Clear previous chunks
    recordedChunksRef.current = [];
    
    const mediaRecorder = new MediaRecorder(localStreamRef.current, options);
    
    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        recordedChunksRef.current.push(event.data);
        setRecordedChunks([...recordedChunksRef.current]);
      }
    };

    mediaRecorder.onstop = () => {
      setIsRecording(false);
      callbacksRef.current.onRecordingStateChange?.(false);
    };

    mediaRecorder.onerror = (event) => {
      console.error('MediaRecorder error:', event);
      callbacksRef.current.onError?.(new Error('Recording error'));
    };

    mediaRecorderRef.current = mediaRecorder;
    mediaRecorder.start(1000); // Collect data every second
    
    setIsRecording(true);
    setRecordedChunks([]);
    callbacksRef.current.onRecordingStateChange?.(true);
  }, []);

  // Stop recording and return blob
  const stopRecording = useCallback((): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      if (!mediaRecorderRef.current || mediaRecorderRef.current.state === 'inactive') {
        reject(new Error('No active recording'));
        return;
      }

      const recorder = mediaRecorderRef.current;
      
      // Store reference to existing chunks and add final chunk handler
      const existingChunks = [...recordedChunksRef.current];
      
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          existingChunks.push(event.data);
        }
      };

      recorder.onstop = () => {
        // Combine all chunks into a single blob
        const blob = new Blob(existingChunks, { type: recorder.mimeType || 'video/webm' });
        
        setIsRecording(false);
        callbacksRef.current.onRecordingStateChange?.(false);
        
        // Clear the chunks ref
        recordedChunksRef.current = [];
        
        resolve(blob);
      };

      // Request any remaining data before stopping
      if (recorder.state === 'recording') {
        recorder.requestData();
      }
      recorder.stop();
    });
  }, []);

  // Download recording
  const downloadRecording = useCallback((blob: Blob, filename?: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || `livestream-${streamId}-${Date.now()}.webm`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [streamId]);

  // Request to watch stream (viewer side)
  const requestStream = useCallback(() => {
    if (!stompClientRef.current?.connected) {
      console.error('WebSocket not connected');
      return;
    }
    
    // Create peer connection first
    createViewerPeerConnection();
    
    // Request stream from broadcaster
    stompClientRef.current.publish({
      destination: `/app/stream/${streamId}/webrtc/request-stream`,
      body: JSON.stringify({ streamId }),
    });
  }, [streamId, createViewerPeerConnection]);

  // Connect to WebSocket
  useEffect(() => {
    if (!streamId) return;

    const wsUrl = `${config.apiBaseUrl.replace('/api', '')}/api/ws/live`;
    
    const client = new Client({
      webSocketFactory: () => new SockJS(wsUrl),
      // Built on every connect attempt, not captured once at construction.
      //
      // connectHeaders is a fixed property, so the token read above was reused by
      // every automatic reconnect for the life of the client. A day-long token
      // survives that; a short-lived one does not - any reconnect after expiry
      // authenticates with a dead token and the stream silently stops, in a retry
      // loop that can never succeed. beforeConnect runs per attempt and can await,
      // so an expired token is refreshed rather than merely re-sent.
      beforeConnect: async () => {
        const fresh = await unifiedAuth.ensureFreshToken();
        client.connectHeaders = fresh ? { Authorization: `Bearer ${fresh}` } : {};
      },
      debug: () => {}, // Suppress STOMP debug logs
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
    });

    // Copy refs for cleanup
    const peerConnections = peerConnectionsRef.current;
    const viewerPeerConnection = viewerPeerConnectionRef;

    client.onConnect = () => {
      setIsConnected(true);

      // Subscribe to WebRTC signals
      client.subscribe(`/topic/stream/${streamId}/webrtc`, (message: IMessage) => {
        const signal: WebRTCSignal = JSON.parse(message.body);
        handleWebRTCSignal(signal);
      });

      // Subscribe to personal WebRTC messages
      client.subscribe('/user/queue/webrtc', (message: IMessage) => {
        const signal: WebRTCSignal = JSON.parse(message.body);
        handleWebRTCSignal(signal);
      });

      // If viewer, request stream after connection
      if (!isBroadcaster) {
        // Small delay to ensure subscriptions are active
        setTimeout(() => {
          requestStream();
        }, 500);
      }
    };

    client.onDisconnect = () => {
      setIsConnected(false);
    };

    client.onStompError = (frame) => {
      console.error('STOMP error:', frame.headers.message);
      callbacksRef.current.onError?.(new Error(frame.headers.message));
    };

    client.activate();
    stompClientRef.current = client;

    return () => {
      if (client.connected) {
        client.deactivate();
      }
      
      // Cleanup peer connections
      peerConnections.forEach((entry) => {
        entry.connection.close();
      });
      peerConnections.clear();
      
      if (viewerPeerConnection.current) {
        viewerPeerConnection.current.close();
        viewerPeerConnection.current = null;
      }
    };
    // Deliberately only the stream and the role.
    //
    // handleWebRTCSignal and requestStream are stable now that the caller's callbacks are
    // read from a ref, and listing them again would reintroduce the loop the moment one
    // of their own dependencies started churning. This effect builds a WebSocket; it must
    // re-run when the thing being connected to changes, and at no other time. The rule
    // is suppressed rather than satisfied because satisfying it is the bug.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamId, isBroadcaster]);

  return {
    isConnected,
    isBroadcasting,
    connectionState,
    viewerCount,
    isScreenSharing,
    isRecording,
    recordedChunks,
    startBroadcast,
    stopBroadcast,
    requestStream,
    startScreenShare,
    stopScreenShare,
    startRecording,
    stopRecording,
    downloadRecording,
  };
};

export default useWebRTC;
